<template>
  <div class='cloud-services-asset-row'>
    <div class='cloud-services-asset-row__content'>
      <div class="main-info" @click="openRow">
        <div class='main-info__service-data'>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.service}"
            offset-y='4px'
            :value="displayedCopiedBadge.service"
          >
            <div class="service-data-text" @click.stop='copyAsset(getServiceName, "service")'> {{ getServiceName }} </div>
          </v-badge>
        </div>
        <div class='main-info__resource-data'>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.resource}"
            offset-y='4px'
            :value="displayedCopiedBadge.resource"
          >
            <div class="resource-data-text" @click.stop='copyAsset(getResourceId, "resource")'> {{ getResourceId }} </div>
          </v-badge>
        </div>
        <div class='main-info__account-data'>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.account}"
            offset-y='4px'
            :value="displayedCopiedBadge.account"
          >
            <div class="account-data-text" @click.stop='copyAsset(getAccountId, "account")'> {{ getAccountId }} </div>
          </v-badge>
        </div>
        <div class='main-info__region-data'>
          <v-badge
            tile
            overlap
            :icon="badgeIcon"
            :content="badgeContent"
            class="manual-config-badge"
            :class="{'copied-badge': displayedCopiedBadge.region}"
            offset-y='4px'
            :value="displayedCopiedBadge.region"
          >
            <div class="region-data-text" @click.stop='copyAsset(getRegion, "region")'> {{ getRegion }} </div>
          </v-badge>
        </div>
        <div class='main-info__compliance-data' :class='{"show-all": showAllCompliance}'>
          <v-chip
            v-for='(compliance, indexCompliance) in complianceForDisplay'
            :key="indexCompliance"
            class='compliance'
            :class="[mapComplianceColor(compliance)]"
          >
            {{ compliance }}
          </v-chip>
        </div>
        <div class='main-info__display-toggle' :class='{ open: showAllCompliance }' v-if='getCompliance.length > ((windowInnerWidth - 400) / 400)'>
          <span text class='hide-compliance-btn' @click.stop="showAllCompliance = !showAllCompliance">
            {{ captionOfComplianceToggleButton }}
          </span>
        </div>
        <div class='right-section'>
          <div class='arrow-expand-section'>
            <v-icon class="expand-icon" :class="{ expand: isOpen }">expand_more</v-icon>
          </div>
        </div>
      </div>
      <div class="details-container" v-if="isOpen">
        <assets-column-headers
          :display='true'
          :headers='headersForServicesTabDetails'
          tab='servicesDetails'
        />
        <cloud-services-asset-details-row
          v-for='(serviceDetails, index) in cloudServiceDetails'
          :item='serviceDetails'
          :key='index'
          :isLast='index === cloudServiceDetails.length - 1'
        />
      </div>
    </div>
  </div>
</template>

<script>
import CloudServicesAssetDetailsRow from '@/components/configuration/CloudServicesAssetDetailsRow.vue';
import AssetsColumnHeaders from '@/components/configuration/AssetsColumnHeaders.vue';
import { mapGetters } from 'vuex';
import { CLEAR_CLOUD_SERVICES_LEVEL_ITEMS, LOAD_CLOUD_SERVICES_LEVEL_ITEMS } from '@/store/project/actionTypes';
import { SECOND_LEVEL } from '@/constants/configuration';

export default {
  name: 'CloudServicesAssetRow',
  props: {
    item: {
      type: Object,
      required: true,
    },
  },
  components: {
    CloudServicesAssetDetailsRow,
    AssetsColumnHeaders,
  },
  data() {
    return {
      showAllCompliance: false,
      headersForServicesTabDetails: [
        'services.tab.details.type.header',
        'services.tab.details.critical.header',
        'services.tab.details.high.header',
        'services.tab.details.medium.header',
        'services.tab.details.low.header',
      ],
      isOpen: false,
      windowInnerWidth: window.innerWidth,
      displayedCopiedBadge: {
        service: false, resource: false, account: false, region: false,
      },
    };
  },
  methods: {
    async openRow() {
      if (!this.isOpen) {
        await this.$store.dispatch(LOAD_CLOUD_SERVICES_LEVEL_ITEMS, {
          domain: this.getTld, serviceName: this.getServiceName, region: this.getRegion, layer: SECOND_LEVEL,
        });
      } else {
        await this.$store.dispatch(CLEAR_CLOUD_SERVICES_LEVEL_ITEMS, {
          domain: this.getTld, serviceName: this.getServiceName, region: this.getRegion, layer: SECOND_LEVEL,
        });
      }
      this.isOpen = !this.isOpen;
    },
    mapComplianceColor(compliance) {
      if (this.item.compliance.FAIL.includes(compliance)) {
        return 'fail';
      }
      if (this.item.compliance.INFO.includes(compliance)) {
        return 'info';
      }
      return 'pass';
    },
    windowResizeHandler() {
      this.windowInnerWidth = window.innerWidth;
    },
    async copyAsset(asset, type) {
      this.$clipboard(asset);
      this.displayedCopiedBadge[type] = true;
      await new Promise(resolve => setTimeout(resolve, 2000));
      this.displayedCopiedBadge[type] = false;
    },
  },
  created() {
    window.addEventListener('resize', this.windowResizeHandler);
  },
  destroyed() {
    window.removeEventListener('resize', this.windowResizeHandler);
  },
  watch: {
    'servicesAssetsPaginationState.page': {
      handler() {
        this.isOpen = false;
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters([
      'cloudServicesSecondLevelItems',
      'servicesAssetsPaginationState',
    ]),
    badgeIcon() {
      return 'build';
    },
    badgeContent() {
      return this.$t('copied');
    },
    captionOfComplianceToggleButton() {
      return this.showAllCompliance ? this.$t('infrastructure.technologies.hide.list') : this.$t('infrastructure.technologies.show.all.list');
    },
    getServiceName() {
      return this.item.serviceName;
    },
    getResourceId() {
      return this.item.resourceId;
    },
    getAccountId() {
      return this.item.accountId;
    },
    getRegion() {
      return this.item.region;
    },
    getCompliance() {
      return Object.keys(this.item.compliance).length > 0 ? Array.from(new Set([...this.item.compliance.FAIL, ...this.item.compliance.PASS, ...this.item.compliance.INFO])) : [];
    },
    getTld() {
      return this.item.tld;
    },
    complianceForDisplay() {
      return this.showAllCompliance ? this.getCompliance : this.getCompliance.slice(0, (this.windowInnerWidth - 350) / 350);
    },
    cloudServiceDetails() {
      const formattedItems = [];
      this.cloudServicesSecondLevelItems.forEach(service => {
        if (service.serviceName === this.getServiceName && service.region === this.getRegion && service.tld === this.getTld) {
          formattedItems.push(service);
        }
      });
      return formattedItems;
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.cloud-services-asset-row {
  min-height: rem(58);
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  padding: $space-1 0;
  align-items: center;

  &__content {
    width: 100%;

    .details-container {
      width: 100%;
      margin-left: 2%;

      &__title {
        font-family: $default-application-font-medium;
        font-size: $font-size-base;
        margin-left: rem(60);
      }
    }

    .main-info {
      align-items: center;
      display: flex;
      color: var(--text-primary);
      cursor: pointer;
      font-family: $default-application-font-medium;
      font-size: $font-size-base;
      line-height: $space-5;
      height: fit-content;
      margin-right: 2.4%;
      flex-direction: row;
      justify-content: space-between;
      margin-left: 3.4%;

      &__service-data {
        width: 10%;
        height: rem(58);
        align-items: center;
        display: flex;

        .manual-config-badge {
          max-width: 80%;

          .service-data-text {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .copied {
            text-transform: capitalize;
            color: var(--text-secondary);
          }
        }
      }

      &__resource-data {
        width: 13%;
        height: rem(58);
        align-items: center;
        display: flex;
        margin-right: 2%;

        .manual-config-badge {
          max-width: 100%;

          .resource-data-text {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .copied {
            text-transform: capitalize;
            color: var(--text-secondary);
          }
        }
      }

      &__account-data {
        width: 15%;
        height: rem(58);
        align-items: center;
        display: flex;

        .manual-config-badge {
          max-width: 80%;

          .account-data-text {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .copied {
            text-transform: capitalize;
            color: var(--text-secondary);
          }
        }
      }

      &__region-data {
        width: 13%;
        height: rem(58);
        align-items: center;
        display: flex;

        .manual-config-badge {
          max-width: 80%;

          .region-data-text {
            max-width: 100%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .copied {
            text-transform: capitalize;
            color: var(--text-secondary);
          }
        }
      }

      &__display-toggle {
        z-index: 5;
        background: $technology-list-toggle-background;
        text-align: right;
        justify-content: center;
        height: rem(40);
        width: 5%;
        padding-top: $space-2;

        .hide-compliance-btn {
          color: $action-button-text-color;
          font-family: $default-application-font-regular;
          font-size: $font-size-base;
          letter-spacing: 0;

          &:hover {
            cursor: pointer;
          }
        }

        &.open {
          position: unset;
        }
      }

      &__compliance-data {
        width: 35%;
        display: flex;
        flex-wrap: nowrap;
        justify-content: left;
        position: relative;
        overflow: hidden;

        &.show-all {
          flex-wrap: wrap;
        }

        .compliance {
          z-index: 5;
          min-width: fit-content;
          max-width: fit-content;
          margin-bottom: $space-0-5;
          background: rgba(43, 175, 105, 0.2);
          color: var(--severity-low);
          text-align: center;
          font-weight: 500;
          font-size: $font-size-xs;
          margin-left: 2%;
          cursor: default;

          &.pass {
            background: $severity-low-bg;
            color: var(--severity-low);
          }

          &.info {
            background: $severity-medium-bg;
            color: var(--severity-medium);
          }

          &.fail {
            background: $severity-critical-bg;
            color: var(--severity-critical);
          }
        }
      }

      .right-section {
        display: flex;
        flex: auto;
        width: 1.5%;
        justify-content: flex-end;

        .arrow-expand-section {
          display: flex;
          justify-content: flex-end;
        }
      }
    }
  }
}
</style>

<style lang='scss'>
@import '~@/assets/styles/design-system';

.cloud-services-asset-row {
  .v-badge.v-badge {
    line-height: initial;
  }

  .assets-column-headers.servicesDetails .header.item_2 {
    position: absolute;
    left: 53.76%;
  }

  .assets-column-headers.servicesDetails .header.item_3 {
    position: absolute;
    left: 63.35%;
  }

  .assets-column-headers.servicesDetails .header.item_4 {
    position: absolute;
    left: 71.84%;
  }

  .assets-column-headers.servicesDetails .header.item_5 {
    position: absolute;
    left: 80.7%;
  }

  .manual-config-badge {
    .v-badge__wrapper {
      .v-badge__badge.v-badge__badge {
        background: transparent;
        border: none;
        color: var(--text-secondary);

        .v-icon {
          font-size: $space-4;
          transform: rotate(180deg);
        }
      }
    }

    &.copied-badge {
      .v-badge__wrapper {
        .v-badge__badge.v-badge__badge {
          text-transform: capitalize;
          background: transparent !important;
          border: none;
          color: var(--brand-primary);
          right: $space-5;
          inset: auto auto calc(100% - 1px) calc(100% - 30px);
        }
      }
    }
  }
}
</style>
